import type { CustomGroup, FiltersGroup } from '@/app/common/filters/core/filtersTypes.ts';
import React, { useMemo, useRef } from 'react';
import type { HandleFilterChanged } from '@/app/common/filters/core/filterComponentType.ts';
import type {
  CustomFilterMainCreators,
  CustomGroupCreators,
} from '@/app/common/filters/surfaces/forms/FilterGroupsComponents.tsx';
import { FilterGroupFilters } from '@/app/common/filters/surfaces/forms/FilterGroupFilters.tsx';
import { FilterGroupContent } from '@/app/common/filters/surfaces/forms/FilterGroupContent.tsx';
import styled, { css } from 'styled-components';
import { getNumberOfColumns, SLOT } from '@/app/common/filters/surfaces/columns.ts';
import {
  collapsesItsOwnOptions,
  FILTER_SURFACE,
} from '@/app/common/filters/surfaces/forms/custom/customFilterMainCreators.ts';
import { isFilterSelected } from '@/app/common/filters/core/filterSelectedUtils.ts';
import { filtersResetValues } from '@/app/common/filters/core/filterResetValues.ts';
import { useTranslation } from '@/app/components/translations/Translation.tsx';
import { focusRing, noSelect } from '@/app/components/search/styles/mixins.ts';
import { useFiltersUiData } from '@/app/common/filters/surfaces/provider/filtersUiProvider.tsx';
import { FILTER_NAME } from '@/app/common/filters/core/filterNames.ts';
import { usePriceFilterTitle } from '@/app/common/filters/surfaces/forms/custom/usePriceFilterScale.ts';
import { filterGroupTitleId } from '@/app/common/filters/surfaces/forms/groupHeading.ts';

type FilterGroupComponentsProps = {
  filtersGroup: FiltersGroup | CustomGroup;
  customGroupCreators?: CustomGroupCreators;
  customFilterMainCreators?: CustomFilterMainCreators;
  isLoading: boolean;
  handleFilterChanged: HandleFilterChanged;
  isModalViewport: boolean;
};

const Title = styled.h3<{ $evo?: boolean }>`
  margin: 0;
  color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};

  ${({ $evo, theme }) =>
    $evo
      ? theme.kallisto.mixins.font('Title3')
      : css`
          font-weight: ${theme.kallisto.font.weight.bold};
          font-size: ${theme.kallisto.font.size['225']};
          line-height: ${theme.kallisto.font.lineHeight['350']};
        `}

  ${({ $evo }) =>
    $evo &&
    css`
      &:focus-visible {
        ${focusRing};
      }
    `}
`;

const HeadingContainer = styled.div<{ $evo?: boolean }>`
  display: flex;
  align-items: ${({ $evo }) => ($evo ? 'baseline' : 'center')};
  justify-content: space-between;
  width: 100%;
  gap: 8px;
  margin-bottom: ${({ $evo, theme }) => ($evo ? theme.kallisto.space.space150 : '8px')};
`;

const ClearButton = styled.button<{ $evo?: boolean }>`
  cursor: pointer;
  background: none;
  color: ${({ theme }) => theme.kallisto.color.text.interactive.textPrimaryDefault};
  ${({ theme }) => theme.kallisto.mixins.font('BodySmall')};
  ${noSelect};
  margin-right: 4px;

  ${({ $evo, theme }) =>
    $evo &&
    css`
      ${theme.kallisto.mixins.font('Body')};
      text-decoration: underline from-font;
      text-underline-position: from-font;
      padding: 0;
      margin-right: 0;
    `}
`;

const GroupContainer = styled.div`
  background: white;
  border-radius: 8px;

  > * {
    width: 100%;
  }
`;

const GroupSection = styled.div<{ $evo?: boolean }>`
  &:not(:last-child) {
    padding-bottom: ${({ $evo, theme }) => ($evo ? theme.kallisto.space.space300 : '16px')};
    border-bottom: 1px solid ${({ theme }) => theme.kallisto.color.border.static.borderDisabled};
  }

  /* With the list's 16px gap, 24px on each side of the divider, as the design spaces sections. */
  ${({ $evo, theme }) =>
    $evo &&
    css`
      &:not(:first-child) {
        padding-top: ${theme.kallisto.space.space100};
      }
    `}
`;

export const FilterGroupComponents = ({
  filtersGroup,
  customGroupCreators,
  customFilterMainCreators,
  isLoading,
  handleFilterChanged,
  isModalViewport,
}: FilterGroupComponentsProps) => {
  const { t } = useTranslation();
  const { columnOverrides = [], useEvoFilters } = useFiltersUiData();
  const priceFilterTitle = usePriceFilterTitle();
  const titleRef = useRef<HTMLHeadingElement>(null);
  const customGroupCreator = customGroupCreators?.[filtersGroup.id];
  const slot = isModalViewport ? SLOT.MOBILE_MODAL : SLOT.ALL_FILTERS;
  const columns = getNumberOfColumns((filtersGroup as FiltersGroup).filters, slot, columnOverrides, useEvoFilters);

  const showsMoreAroundFilters =
    !!(filtersGroup as FiltersGroup).showMore &&
    !(filtersGroup as FiltersGroup).filters?.every((filter) =>
      collapsesItsOwnOptions(filter.name, FILTER_SURFACE.ALL_FILTERS, useEvoFilters)
    );

  const hasFilterComponents =
    'filters' in filtersGroup && 'label' in filtersGroup && filtersGroup.filters && filtersGroup.filters.length > 0;

  const hasAnyFilterSelected = useMemo(() => {
    if (!hasFilterComponents) return false;
    return filtersGroup.filters.some((filter) => isFilterSelected(filter));
  }, [filtersGroup, hasFilterComponents]);

  const handleClearFilters = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent accordion toggle
    if (!hasFilterComponents) return;

    const clearedFilters = filtersResetValues({ filters: filtersGroup.filters });
    handleFilterChanged(clearedFilters);
    titleRef.current?.focus();
  };

  if (customGroupCreator) {
    const customLabel = 'label' in filtersGroup ? filtersGroup.label : undefined;
    if (!customLabel) return customGroupCreator();

    return (
      <GroupSection $evo={useEvoFilters}>
        <HeadingContainer $evo={useEvoFilters}>
          <Title $evo={useEvoFilters} id={filterGroupTitleId(filtersGroup.id)}>
            {customLabel}
          </Title>
        </HeadingContainer>
        {customGroupCreator()}
      </GroupSection>
    );
  }

  if (hasFilterComponents) {
    const isPriceRangeGroup =
      filtersGroup.filters.length === 1 && filtersGroup.filters[0]?.name === FILTER_NAME.PRICE_RANGE;
    const title = (isPriceRangeGroup && priceFilterTitle) || filtersGroup.label;
    const heading = (
      <HeadingContainer $evo={useEvoFilters}>
        <Title ref={titleRef} tabIndex={-1} $evo={useEvoFilters}>
          {title}
        </Title>
        {hasAnyFilterSelected && (
          <ClearButton $evo={useEvoFilters} onClick={handleClearFilters} data-testid="clear-button">
            {t('vacation.oss.filters.clear')}
          </ClearButton>
        )}
      </HeadingContainer>
    );

    return (
      <GroupSection $evo={useEvoFilters}>
        {heading}
        <GroupContainer>
          <FilterGroupContent filtersGroup={filtersGroup} showMore={showsMoreAroundFilters}>
            {(filters) => (
              <FilterGroupFilters
                filters={filters}
                customFilterMainCreators={customFilterMainCreators}
                isLoading={isLoading}
                handleFilterChanged={handleFilterChanged}
                columns={columns}
                surface={FILTER_SURFACE.ALL_FILTERS}
                sectionTitle={title}
              />
            )}
          </FilterGroupContent>
        </GroupContainer>
      </GroupSection>
    );
  }
  return null;
};
